import { useEffect, useMemo, useState } from 'react';
import { Tag, Button, Typography, Spin, message } from 'antd';
import { EditOutlined, AppstoreOutlined } from '@ant-design/icons';
import {
  listAgentCategories,
  type AgentCategoryRow,
} from '@/api/lookups';
import { updateAgent } from '@/api/agents';
import type { AgentDefinition } from '@/api/types';
import { trackEvent } from '@/lib/telemetry';
import { AgentCategoryPickerDrawer } from './AgentCategoryPickerDrawer';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// AgentCategoryEditor — read-mode tag with an Edit button that opens the
// AgentCategoryPickerDrawer.
//
// The drawer handles search + list + inline-create. This file only:
//   • Resolves the active category row for the read-mode Tag (lazy-fetched
//     from /api/AiAgentCategory/Search on mount).
//   • Owns the write side: takes the picker's onSelect callback, calls
//     updateAgent, and bubbles the saved entity back to the parent via
//     onSaved so the in-memory store stays in sync.
//
// Why split picker vs. editor: the picker is reusable from anywhere that
// needs to choose a category (future: a bulk-edit drawer, an Operations
// "uncategorized agents" sweep tool). Keeping it presentational with a
// callback contract makes it cheap to re-mount.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  agent: AgentDefinition;
  onSaved?: (next: AgentDefinition) => void | Promise<void>;
  /** Currently unused; kept so the call-site stays stable as the editor
   * gains theme-aware bits later. */
  isDark?: boolean;
}

export function AgentCategoryEditor({ agent, onSaved }: Props) {
  // ── Lookup state ──────────────────────────────────────────────────────
  // Cached for the read-mode Tag. The picker drawer maintains its own
  // copy + refresh cycle (the two views can diverge briefly after a
  // create — the picker re-fetches on open, this editor refreshes after
  // commitCategory).
  const [categories, setCategories] = useState<AgentCategoryRow[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const reloadCategories = async (): Promise<AgentCategoryRow[]> => {
    setLoadingCategories(true);
    try {
      const rows = await listAgentCategories();
      setCategories(rows);
      return rows;
    } catch {
      return categories;
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    void reloadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCategory = useMemo<AgentCategoryRow | null>(() => {
    if (agent.categoryId == null) return null;
    return categories.find((c) => c.id === agent.categoryId) ?? null;
  }, [categories, agent.categoryId]);

  // ── Picker drawer state ───────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const commitCategory = async (categoryId: number): Promise<void> => {
    if (categoryId === agent.categoryId) {
      setPickerOpen(false);
      return;
    }
    setSaving(true);
    try {
      const next = { ...agent, categoryId };
      const saved = await updateAgent(next);
      // Refresh the local lookup so the read-mode Tag picks up any
      // newly-created category that the picker just added.
      await reloadCategories();
      await onSaved?.(saved);
      message.success('Category updated');
      trackEvent('AgentCategory.Changed', { agentId: agent.id, categoryId });
      setPickerOpen(false);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {loadingCategories && categories.length === 0 ? (
          <Spin size="small" />
        ) : activeCategory ? (
          <Tag
            color={activeCategory.color || 'blue'}
            icon={<AppstoreOutlined />}
            style={{ margin: 0 }}
          >
            {activeCategory.name}
          </Tag>
        ) : agent.categoryId != null ? (
          <Tag color="default" style={{ margin: 0 }}>
            Category #{agent.categoryId}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Uncategorized
          </Text>
        )}
        <Button
          size="small"
          type="text"
          icon={<EditOutlined />}
          loading={saving}
          onClick={() => setPickerOpen(true)}
          aria-label="Edit category"
        >
          Edit
        </Button>
      </div>

      <AgentCategoryPickerDrawer
        open={pickerOpen}
        onClose={() => { if (!saving) setPickerOpen(false); }}
        currentCategoryId={agent.categoryId}
        // The editor never renders the "Any category" clear row
        // (clearLabel unset), so the picker can only fire onSelect with a
        // real row here. We still narrow defensively because the prop
        // type allows null.
        onSelect={(c) => { if (c) return commitCategory(c.id); }}
      />
    </>
  );
}
